import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { Row, Col, FormGroup, Label } from "reactstrap";

import { GetLanguageString } from "../helper/Components";
import { GetSelectedLanguage } from "../helper/Method";
import { ApiUnit } from "../unit/ApiUnit";
import { encryptStorage } from "../../constants/EncryptStorage";

const URDU_FONT = "'Mehr Nastaleeq'";
const USRA_UNIT_TYPES = ["Usra", "UsraMuavin", "Munfarid"];

const applyLocalSelection = (prev, record) => {
  let foundIndex = -1;
  const next = prev.map((level, index) => {
    const inList = level.unitList?.some((u) => u.id === record.value);
    if (!inList) {
      return level;
    }
    foundIndex = index;
    return {
      ...level,
      selectedUnit: {
        id: record.value,
        name: record.label,
        unitTypeName: record.label1,
      },
    };
  });

  if (foundIndex === -1) {
    return prev;
  }

  // Drop any levels below the selected unit (e.g. leftover Usra list).
  return next.slice(0, foundIndex + 1);
};

const SelectUnit = ({
  setValues,
  values,
  unitId = 0,
  isDeleted = false,
  excludeExUnit = true,
  isDisabled = false,
  inlineWithArrows = false,
  filterUnitTypes = null,
  excludeUnitTypes = null,
}) => {
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const [unitList, setUnitList] = useState([]);
  const skipFetchRef = useRef(false);
  if (unitId === 0) unitId = loggedInUnitId;
  const unitTypeName = "null";
  const hideUsra = Boolean(
    excludeUnitTypes?.some((t) => USRA_UNIT_TYPES.includes(t)),
  );

  // Leaf for this picker: Maqami Tanzeem when Usras are excluded — no child fetch.
  const isStopUnitType = (unitType) =>
    hideUsra && (unitType === "Tanzeem" || USRA_UNIT_TYPES.includes(unitType));

  useEffect(() => {
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      return;
    }

    ApiUnit()
      .getUnitList(
        unitId,
        loggedInUnitId,
        isDeleted,
        unitTypeName,
        excludeExUnit,
        new Date().toISOString(),
        hideUsra,
      )
      .then((res) => {
        setUnitList(res.data);
      })
      .catch((err) => console.log(err));
  }, [unitId, loggedInUnitId, isDeleted, excludeExUnit, hideUsra]);

  const handleChangeUnits = (record) => {
    if (record === null) {
      return;
    }

    if (isStopUnitType(record.label1)) {
      // Hierarchy + options are already loaded; only mark selection locally.
      skipFetchRef.current = true;
      setUnitList((prev) => applyLocalSelection(prev, record));
      setValues({
        ...values,
        unit: record,
      });
      return;
    }

    setValues({
      ...values,
      unit: record,
    });
  };

  const matchesUnitTypeFilter = (unitType, index, totalLevels) =>
    (!filterUnitTypes ||
      index < totalLevels - 1 ||
      filterUnitTypes.includes(unitType)) &&
    (!excludeUnitTypes || !excludeUnitTypes.includes(unitType));

  const buildUnitOptions = (record, index, totalLevels) => {
    const unitOptions = [];
    record.unitList.forEach((d) => {
      if (matchesUnitTypeFilter(d.unitTypeName, index, totalLevels)) {
        unitOptions.push({
          value: d.id,
          label: d.name,
          label1: d.unitTypeName,
        });
      }
    });
    return unitOptions;
  };

  const renderSelect = (unitOptions, selected) => (
    <Select
      isDisabled={isDisabled}
      options={unitOptions}
      value={Object.keys(selected).length ? selected : null}
      className="basic-single ur"
      classNamePrefix="select"
      onChange={handleChangeUnits}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base) => ({ ...base, fontFamily: URDU_FONT }),
        singleValue: (base) => ({ ...base, fontFamily: URDU_FONT }),
        input: (base) => ({ ...base, fontFamily: URDU_FONT }),
        placeholder: (base) => ({ ...base, fontFamily: URDU_FONT }),
      }}
    />
  );

  if (inlineWithArrows) {
    const visibleLevels = unitList
      .map((record, index) => {
        const unitOptions = buildUnitOptions(record, index, unitList.length);
        if (unitOptions.length === 0) {
          return null;
        }

        let selected = {};
        if (record.selectedUnit !== null) {
          const selectedType = record.selectedUnit.unitTypeName;
          if (
            excludeUnitTypes &&
            excludeUnitTypes.includes(selectedType) &&
            !unitOptions.some((o) => o.value === record.selectedUnit.id)
          ) {
            selected = {};
          } else {
            selected = {
              value: record.selectedUnit.id,
              label: record.selectedUnit.name,
              label1: record.selectedUnit.unitTypeName,
            };
          }
        }

        return { record, index, unitOptions, selected };
      })
      .filter(Boolean);

    return (
      <div className="d-flex flex-wrap align-items-center m-b-md">
        {visibleLevels.map((level, visibleIndex) => {
          const isLastIndex = visibleIndex === visibleLevels.length - 1;
          return (
            <React.Fragment key={level.index}>
              <div style={{ minWidth: 180, flex: "0 0 auto" }}>
                {renderSelect(level.unitOptions, level.selected)}
              </div>
              {isLastIndex ? null : (
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ width: 40, height: 38, flex: "0 0 auto" }}
                >
                  {GetSelectedLanguage() === "en" ? (
                    <i className="fa fa-arrow-right fa-lg" />
                  ) : (
                    <i className="fa fa-arrow-left fa-lg" />
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <>
      <Row>
        {unitList.map((record, index) => {
          const unitOptions = buildUnitOptions(record, index, unitList.length);

          if (unitOptions.length === 0) {
            return null;
          }
          let selected = {};
          let unitName = "";
          if (record.parentUnitName !== null) unitName = record.parentUnitName;
          if (record.selectedUnit !== null) {
            selected = {
              value: record.selectedUnit.id,
              label: record.selectedUnit.name,
              label1: record.selectedUnit.unitTypeName,
            };
          }

          return (
            <Col xl="2" key={index}>
              <FormGroup>
                <Label className="col-form-label">
                  {unitName !== "" ? (
                    <GetLanguageString
                      props="member_select_unit_name"
                      value={unitName}
                    />
                  ) : (
                    <GetLanguageString props="member_select_unit" />
                  )}
                </Label>
                {renderSelect(unitOptions, selected)}
              </FormGroup>
            </Col>
          );
        })}
      </Row>
    </>
  );
};

export default SelectUnit;
